import { useState } from 'react'
import type { MuscleBand, BodyMapRegion } from '../../api/types'
import { MuscleFigure } from '../muscle/MuscleFigure'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { Panel, TOKENS } from '../ui/dashboard-primitives'
import { useBodyMapPanel } from './panelStates'

const BAND_COLOR: Record<MuscleBand, string> = {
  low: TOKENS.terracotta,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  mid: 'rgba(245,237,225,0.30)',
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  near: 'rgba(245,237,225,0.52)',
  over: TOKENS.amber,
}
const BAND_LABEL: Record<MuscleBand, string> = {
  low: 'Below 50%',
  mid: '50–85%',
  near: '85–100%',
  over: 'At / over',
}
const prettySlug = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

function Figure({
  side,
  caption,
  bands,
  hover,
  setHover,
}: {
  side: 'front' | 'back'
  caption: string
  bands: Record<string, MuscleBand>
  hover: string | null
  setHover: (s: string | null) => void
}) {
  return (
    <figure className="flex flex-col items-center gap-2.5 m-0">
      <MuscleFigure
        side={side}
        bands={bands}
        interactive
        hoverSlug={hover}
        onRegionEnter={setHover}
        onRegionLeave={() => setHover(null)}
        className="w-full max-w-45.5 h-auto overflow-visible"
      />
      <figcaption
        className="text-[0.5625rem] tracking-[0.22em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
      >
        {caption}
      </figcaption>
    </figure>
  )
}

function weakestOf(regions: BodyMapRegion[]): BodyMapRegion | null {
  return regions.length ? regions.reduce((a, b) => (b.pct_of_reference < a.pct_of_reference ? b : a)) : null
}

/** Where the gap sits, front and back. The panel and its heading stay up in
 *  every state; the figures go through `AsyncPanel` (Spec 2 D5, #170), so a
 *  failed request no longer draws as unshaded figures, which read as a body
 *  with no gaps at all. */
export function BodyMapPanel() {
  const bodyMap = useBodyMapPanel()
  const regions: BodyMapRegion[] = bodyMap.data?.regions ?? []
  const weakest = weakestOf(regions)

  return (
    <Panel variant="light">
      <div className="flex items-baseline justify-between gap-3">
        <h4
          className="text-[0.5625rem] tracking-[0.28em] uppercase font-medium"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          Where the gap sits
        </h4>
        {weakest && weakest.band === 'low' && (
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase border whitespace-nowrap"
            style={{
              fontFamily: 'var(--font-mono)',
              backgroundColor: TOKENS.terracottaSoft,
              color: TOKENS.terracotta,
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              borderColor: 'rgba(198,110,77,0.32)',
            }}
          >
            {prettySlug(weakest.slug)} lowest
          </span>
        )}
      </div>

      <AsyncPanel
        sources={bodyMap.sources}
        hasContent={bodyMap.hasContent}
        subject="Your body map"
        skeleton={
          <PanelSkeleton label="Loading body map">
            <div className="mt-4">
              <Shimmer height={260} />
            </div>
          </PanelSkeleton>
        }
      >
        {() => <Figures regions={regions} weakest={weakest} />}
      </AsyncPanel>
    </Panel>
  )
}

function Figures({ regions, weakest }: { regions: BodyMapRegion[]; weakest: BodyMapRegion | null }) {
  const [hover, setHover] = useState<string | null>(null)
  const bands = Object.fromEntries(regions.map((r) => [r.slug, r.band])) as Record<string, MuscleBand>
  const bySlug = Object.fromEntries(regions.map((r) => [r.slug, r]))
  const hot = hover ? bySlug[hover] : null

  return (
    <>
      <div className="grid grid-cols-2 gap-x-3 mt-4">
        <Figure side="front" caption="Anterior" bands={bands} hover={hover} setHover={setHover} />
        <Figure side="back" caption="Posterior" bands={bands} hover={hover} setHover={setHover} />
      </div>

      {/* Tooltip line: the hovered region, else the weakest as a resting hint. */}
      <p className="mt-4 min-h-5 text-[0.8125rem]" style={{ color: TOKENS.creamSoft }}>
        {hot ? (
          <>
            <b style={{ color: TOKENS.cream }}>{prettySlug(hot.slug)}</b> · {hot.pct_of_reference.toFixed(1)}% of
            reference · {hot.station.replace(/^Q\s+/, '')}
          </>
        ) : weakest ? (
          <span style={{ color: TOKENS.creamDim }}>Hover a region · weakest is {prettySlug(weakest.slug)}.</span>
        ) : null}
      </p>

      <div className="flex flex-wrap gap-x-3.5 gap-y-2 mt-3">
        {(['low', 'mid', 'near', 'over'] as MuscleBand[]).map((b) => (
          <span
            key={b}
            className="inline-flex items-center gap-1.75 text-[0.5625rem] tracking-[0.14em] uppercase"
            style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
          >
            <i className="block w-2.25 h-2.25 rounded-sm" style={{ background: BAND_COLOR[b] }} />
            {BAND_LABEL[b]}
          </span>
        ))}
      </div>
    </>
  )
}
